import styled from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

/**
 * Shared section styles for the long-form SEO body under a free tool.
 * Same visual language as the existing per-tool content styles, extracted so
 * the fake chat generators can reuse one copy instead of four.
 */

export const StyledContentWrapper = styled.div`
  background: ${CSSVars.white};
`;

export const StyledSection = styled.section<{ bg?: string }>`
  padding: 60px 20px;
  background: ${({ bg }) => bg || CSSVars.white};
  border-top: 1px solid rgba(0, 0, 0, 0.07);

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 80px 30px;
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 110px 40px;
  }
`;

export const StyledInner = styled.div<{ maxWidth?: string }>`
  max-width: ${({ maxWidth }) => maxWidth || '960px'};
  margin: 0 auto;
`;

export const StyledSectionLabel = styled.p`
  font-size: ${CSSVars.fontSizeXS};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${CSSVars.blue500};
  margin-bottom: 14px;
`;

export const StyledSectionHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${CSSVars.black};
  margin-bottom: 18px;
  line-height: 1.25;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    font-size: ${CSSVars.fontSize2XL};
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    font-size: ${CSSVars.fontSize3XL};
  }
`;

export const StyledSectionIntro = styled.p`
  font-size: ${CSSVars.fontSizeMD};
  color: ${CSSVars.gray600};
  line-height: 1.75;
  max-width: 720px;
  font-weight: 300;
`;

/* ── How It Works ────────────────────────────────────────────────────────── */
export const StyledStepsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 44px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`;

export const StyledStep = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  padding: 24px 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);

  .step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    border-radius: 50%;
    font-weight: 700;
    margin-bottom: 16px;
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    font-weight: 300;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 28px 24px;
  }
`;

/* ── Spec Box (dark accent) ───────────────────────────────────────────────── */
export const StyledSpecBox = styled.div`
  background: linear-gradient(135deg, #09093a 0%, #0d0e20 100%);
  border: 1px solid rgba(76, 79, 255, 0.3);
  border-radius: 16px;
  padding: 24px 22px;
  margin: 28px 0;

  .spec-line {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.white};
    margin-bottom: 10px;
    font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  }

  .spec-example {
    font-size: ${CSSVars.fontSizeSM};
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;

    .accent {
      color: ${CSSVars.pink300};
      font-weight: 700;
    }
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 32px 36px;
  }
`;

export const StyledBodyParagraph = styled.p`
  font-size: ${CSSVars.fontSizeMD};
  color: ${CSSVars.gray700};
  line-height: 1.85;
  margin-top: 16px;
  font-weight: 300;
`;

/* ── Reference Tables ─────────────────────────────────────────────────────── */
export const StyledDataTable = styled.div`
  margin-top: 36px;
  overflow-x: auto;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 480px;
  }

  thead tr {
    background: linear-gradient(90deg, #09093a 0%, #0d0e30 100%);
  }

  thead th {
    color: rgba(255, 255, 255, 0.85);
    padding: 14px 18px;
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    text-align: left;
  }

  tbody tr {
    background: ${CSSVars.white};
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: rgba(76, 79, 255, 0.04);
    }
  }

  tbody td {
    padding: 14px 18px;
    color: ${CSSVars.gray700};
    font-size: ${CSSVars.fontSizeSM};
  }

  .col-name {
    font-weight: 600;
    color: ${CSSVars.black};
  }

  .col-value {
    font-weight: 700;
    color: ${CSSVars.blue500};
  }

  .badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .badge-good {
    background: rgba(34, 197, 94, 0.12);
    color: #16a34a;
  }
  .badge-warn {
    background: rgba(251, 146, 60, 0.15);
    color: #c2410c;
  }
  .badge-info {
    background: rgba(76, 79, 255, 0.12);
    color: ${CSSVars.blue500};
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    tbody td,
    thead th {
      padding: 16px 20px;
      font-size: ${CSSVars.fontSizeSM};
    }
  }
`;

export const StyledSourcesNote = styled.p`
  margin-top: 14px;
  font-size: ${CSSVars.fontSizeXS};
  color: ${CSSVars.gray500};
  font-style: italic;
`;

/* ── Common Mistakes / Factor cards ───────────────────────────────────────── */
export const StyledFactorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  margin-top: 36px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 22px;
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledFactorCard = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 22px 18px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

  .factor-icon {
    font-size: 28px;
    display: block;
    margin-bottom: 14px;
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    font-weight: 300;
  }

  .factor-stat {
    display: inline-block;
    margin-top: 12px;
    background: rgba(76, 79, 255, 0.08);
    color: ${CSSVars.blue500};
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 6px;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 28px 24px;
  }
`;

/* ── Best Practices / Tips ────────────────────────────────────────────────── */
export const StyledTipsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  margin-top: 36px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 22px;
  }
`;

export const StyledTip = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 22px 18px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);

  .tip-number {
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    color: ${CSSVars.blue500};
    letter-spacing: 0.1em;
    margin-bottom: 8px;
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    font-weight: 300;
  }

  a {
    display: inline-block;
    margin-top: 10px;
    color: ${CSSVars.blue500};
    font-weight: 700;
    font-size: ${CSSVars.fontSizeSM};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 28px 24px;
  }
`;

/* ── Glossary ─────────────────────────────────────────────────────────────── */
export const StyledGlossaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-top: 36px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }
`;

export const StyledGlossaryItem = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 18px 16px;

  dt {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 6px;
  }

  dd {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    font-weight: 300;
    margin: 0;
  }
`;

/* ── Related Articles ─────────────────────────────────────────────────────── */
export const StyledRelatedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  margin-top: 36px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 22px;
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledRelatedCard = styled.a`
  display: block;
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 22px 18px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-2px);
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
    line-height: 1.35;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.7;
    font-weight: 300;
    margin-bottom: 14px;
  }

  .read-cta {
    color: ${CSSVars.blue500};
    font-weight: 700;
    font-size: ${CSSVars.fontSizeSM};
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 28px 24px;
  }
`;
